/* file description */
import { useEffect } from 'react';
import { Button, Modal } from 'antd';
import { useAsyncEffect } from 'ahooks';
import { queryProcessData, postRecordList } from '@/components/SPSteps/components/SPSteps/effects/process.service';
import useMetaStore from '@/store/useMetaStore';
import { getCopyMapField } from '@/asset/AssetTask/service';
import useValuesStore from '@/store/useValuesStore';
import _ from 'lodash';
import { UserInfo, getUserInfoCache } from '@/api/system';
import { getFormatData, getEndNode } from '@/services/stepNodes.service';

export const useLoad = ({
  version,
}: {
  version: string;
}) => {
  const { taskId, recordId, utils, taskInfo, pageType, pageConfig, emitEvent } = useMetaStore();
  const { values } = useValuesStore();
  const [modal, loadingHolder] = Modal.useModal();

  const jumpUrl = () => {
    const openVersion = utils?.queryParams?.openVersion === 'true' ? true : undefined;
    let url = `/lcp/task/${taskId}/detail/${recordId}`;
    if (openVersion) {
      url += `?openVersion=true&version=${version}`;
    }

    utils?.buiNavigate({
      type: 'spa',
      path: url,
      title: 'EIMOS',
    });
  };

  const countDown = () => {
    let secondsToGo = 5;

    const instance = modal.warning({
      title: '当前页面正在审批中,不可编辑',
      content: `还有 ${secondsToGo} 秒跳转到详情页`,
      okText: '现在就去',
      centered: true,
      onOk: (close) => {
        close();
        timer && clearInterval(timer);
        instance?.destroy?.();
        jumpUrl();
      },
    });

    const timer = setInterval(() => {
      secondsToGo -= 1;
      instance.update({
        content: `还有 ${secondsToGo} 秒跳转到详情页`,
      });
    }, 1000);

    setTimeout(() => {
      clearInterval(timer);
      instance.destroy();
      jumpUrl();
    }, secondsToGo * 1000);
  };
  const matchRole = (userRoles: any[], currentConfig: { assigneeRole?: any[] | undefined }) => {
    const { assigneeRole = [] } = currentConfig;
    return userRoles.some((role) => assigneeRole.includes(role));
  };
  const matchStaff = (userAccount: any, currentConfig: { assigneeStaff?: any[] | undefined }) => {
    const { assigneeStaff = [] } = currentConfig;
    return assigneeStaff.includes(userAccount);
  };
  const matchFeild = (userAccount: any, currentConfig: { assigneeField?: string | undefined }) => {
    const { assigneeField = '' } = currentConfig;
    const arr = assigneeField.split('-');
    const dataName = arr[0];
    const fieldList = arr[1].split(',');
    const data = values?.[dataName] || [];
    const keys = Object.keys(data);
    const valueList: any[] = [];
    fieldList.forEach((item: string) => {
      if (keys.includes(item)) {
        valueList.push(data[item as keyof typeof data]);
      }
    });
    return valueList.includes(userAccount);
  };
  /**
   * 编辑页如果发现当前页面在审批中，则要进行跳走
   */
  useAsyncEffect(async () => {
    if (pageType === 'edit') {
      const res = await queryProcessData({
        taskId,
        dataId: recordId,
        version,
      });
      const resNodeList = res?.nodes;
      const finished = res?.finished;
      if (!resNodeList || finished) return;
      const proRes = await postRecordList({
        app_id: 'tech',
        model_id: 'approval_design',
        relation_task_id: taskId,
      });
      const resList = proRes?.data ? (proRes?.data[0]?.schema_values?.nodes || []) : [];
      const edgeList = proRes?.data ? (proRes?.data[0]?.schema_values?.edges || []) : [];
      const nodeList = resList.map((el: any) => {
        const name = el?.data?.basicConfiguration?.name;
        //  "SEQUENTIAL" 1顺序签 "PARALLEL" 2会签 "OR" 或签
        const type = el?.data?.basicConfiguration?.approvalMode;
        return {
          name,
          title: {
            label: name,
            approvalType: type,
          },
          id: el?.id,
          ...(el || {}),
        };
      });
      resNodeList.forEach((el: any) => {
        const node = nodeList.find((item: any) => item.id === el.nodeId);
        const approvalType = node?.title?.approvalType;
        const noComplete = el.comments?.filter((com: any) => !com.complete) || [];
        const isComplete = el.comments?.filter((com: any) => com.complete);
        if (approvalType === 'OR') {
          el.complete = isComplete.length > 0;
        } else {
          el.complete = noComplete?.length === 0;
        }
      });
      const renderList = _.cloneDeep(nodeList).map((el: any) => {
        const comData = (res?.nodes || []).find((nod: any) => nod.nodeId === el.id);
        const elData = {
          ...el,
          ...(comData || {}),
          showNode: !!comData || el?.shape === 'start-node',
        };
        const rederData = getFormatData(elData);
        return rederData;
      });
      const isFinish = !resNodeList.find((el: any) => el.complete === false);
      const newProcessData = renderList.filter((el: any) => el.showNode);
      const isError = !!newProcessData.find((el: any) => el.isRefuse);
      if (isFinish && !isError && resNodeList.length > 0) {
        const ativeId = newProcessData[newProcessData.length - 1].id;
        const endNode = getEndNode(ativeId, edgeList, nodeList);
        if (endNode) {
          newProcessData.push(endNode);
        }
      }

      const userInfo: UserInfo = await getUserInfoCache();
      const userRoles = userInfo?.myRoles.map((role) => role.roleNumber) ?? [];
      const userAccount = userInfo?.user?.userAccount;
      const nodeLength = newProcessData.length;
      const newCurrent = (isFinish && !isError && resNodeList.length > 0) ? nodeLength : nodeLength - 1;
      const currentStepConfig = newProcessData[newCurrent]?.data?.basicConfiguration ?? {};
      const { authority = '' } = currentStepConfig;
      let canEdit = false;
      if (currentStepConfig.assigneeType === 'NODE') {
        const assigneeNode = nodeList.find((node: { id: any }) => (node.id === currentStepConfig.assigneeNode));
        const currentConfig = assigneeNode?.data?.basicConfiguration ?? {};
        if (currentConfig.assigneeType === 'FIELD') {
          canEdit = authority === 'EDITABLE' && matchFeild(userAccount, currentConfig);
        }
        if (currentConfig.assigneeType === 'ROLE') {
          canEdit = authority === 'EDITABLE' && matchRole(userRoles, currentConfig);
        }
        if (currentConfig.assigneeType === 'STAFF') {
          canEdit = authority === 'EDITABLE' && matchStaff(userAccount, currentConfig);
        }
        if (currentStepConfig.assigneeFilterType === 'ROLE') {
          const { assigneeFilterType = [] } = currentStepConfig;
          const matchFilterRole = userRoles.some((role: any) => assigneeFilterType.includes(role));
          canEdit = canEdit && matchFilterRole;
        }
      }
      if (currentStepConfig.assigneeType === 'FIELD') {
        canEdit = authority === 'EDITABLE' && matchFeild(userAccount, currentStepConfig);
      }
      if (currentStepConfig.assigneeType === 'ROLE') {
        canEdit = authority === 'EDITABLE' && matchRole(userRoles, currentStepConfig);
      }
      if (currentStepConfig.assigneeType === 'STAFF') {
        canEdit = authority === 'EDITABLE' && matchStaff(userAccount, currentStepConfig);
      }
      if (!canEdit) countDown();
    }
  }, []);

  /**
   * 如果是复制页则需要触发所有的event TODO: 当前只触发了头的change事件，并没有触发复制行的change事件
   */
  useEffect(() => {
    if (!(utils?.queryParams.copyId && utils?.queryParams?.copyName)) return;
    const targetCopyMap = pageConfig?.createPageConfig?.copyMap?.find((item) => item.name === utils?.queryParams?.copyName);
    const { targetMapModuleField } = getCopyMapField(targetCopyMap);
    Object.keys(targetMapModuleField).forEach((moduleCode) => {
      const moduleVal = values?.[moduleCode];
      const filedList = targetMapModuleField[moduleCode];
      if (!_.isArray(moduleVal)) {
        filedList.forEach((fieldCode: string) => {
          emitEvent({ value: moduleVal?.[fieldCode], moduleName: moduleCode, fieldName: fieldCode, touchConfig: 'change', type: 'light' });
        });
      }
    });
  }, []);

  return {
    loadingHolder,
  };
};
